"use client";

import { IconArrowBarRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { logoUrl } from "@/features/media/mediaUrl";
import { useMediaContext } from "@/features/media/useMediaContext";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useSidebar } from "@/hooks/useSidebar";
import { cn } from "@/lib/cn";

/**
 * The collapsed sidebar's handle: the tenant's first logo plus a re-open control.
 *
 * Width-collapsed rather than unmounted, so the logo slides out of view with the
 * panel. `isAnimated` withholds the transition until the panel is moved —
 * otherwise every load animates the handle open from nothing.
 *
 * Out of the tab order while the sidebar is open: it's still in the layout at zero
 * width, and a focusable control in a zero-width box is a keyboard trap with
 * nothing to show for it.
 */
export function SidebarToggle() {
  const t = useTranslations();
  const configs = useProjectConfigs();
  const { isOpen, toggle, isAnimated } = useSidebar();
  const { domain } = useMediaContext();

  const { appLogos, sidebarCollapse, hideSidebar } = configs;
  const logo = logoUrl(appLogos?.[0]?.image?.fileName, domain);

  return (
    <div
      className={cn(
        "flex min-w-0 shrink-0 justify-end overflow-hidden",
        isAnimated && "transition-[max-width] duration-300",
        isOpen ? "max-w-0" : "max-w-[400px]"
      )}
    >
      <button
        type="button"
        tabIndex={isOpen ? -1 : 0}
        disabled={!sidebarCollapse}
        onClick={toggle}
        className="sidebar-toggle-container pointer-events-auto inline-flex h-[3.5rem] flex-shrink-0 items-center gap-6 bg-white dark:bg-dark-color"
      >
        {logo && (
          // Tenant-uploaded, arbitrary dimensions, same-origin — see the note in
          // Sidebar.tsx.
          // eslint-disable-next-line @next/next/no-img-element
          <img className="h-[36px]" src={logo} alt={t("logo")} />
        )}

        {/* A locked-open sidebar has nothing to expand, so the label would be a
            lie; a locked-*closed* one still needs it. */}
        {!sidebarCollapse && hideSidebar ? null : (
          <span className="sidebar-toggle flex items-center gap-2 text-sm text-neutral-800 dark:text-off-white">
            <IconArrowBarRight className="text-neutral-500" aria-hidden="true" />
            {t("sidebar.expand")}
          </span>
        )}
      </button>
    </div>
  );
}
